import { hashInvestmentReverse } from "../../component/Methods";
import { SMA_BUCKET_STRATEGY_ID } from "./utility";

export const OPTION_PUTS_CALLS = [
  { label: "Puts", value: 1 },
  { label: "Calls", value: 0 },
];

export function getActionAndPosition(action) {
  switch (action) {
    case "Buy":
      return { action: 0, position: 0 };
    case "Sell":
      return { action: 3, position: 0 };
    case "Short":
      return { action: 3, position: 1 };
    case "Cover":
      return { action: 0, position: 1 };
    default:
      return { action: 0, position: 0 };
  }
}

export function getTradeActionOptions(showOptionFields) {
  return showOptionFields
    ? [
        { value: "Buy", label: "Buy to Open" },
        { value: "Cover", label: "Buy to Close" },
        { value: "Sell", label: "Sell to Close" },
        { value: "Short", label: "Sell to Open" },
      ]
    : [
        { value: "Buy", label: "Buy" },
        { value: "Cover", label: "Cover" },
        { value: "Sell", label: "Sell" },
        { value: "Short", label: "Short" },
      ];
}

export function actionFromActionPosition(action, position) {
  if (action === "Sell" && position === "Short") return "Short";
  if (action === "Buy" && position === "Short") return "Cover";
  if (action === "Sell" && position === "Long") return "Sell";
  if (action === "Buy" && position === "Long") return "Buy";
  return action || "";
}

export function sortFundsByAbbreviation(funds, { requireAbbreviation = false } = {}) {
  const list = funds ? [...funds] : [];
  const filtered = requireAbbreviation
    ? list.filter((fund) => fund && fund.abbreviation)
    : list;
  return filtered.sort((a, b) =>
    (a.abbreviation || "").localeCompare(b.abbreviation || "")
  );
}

export function sortBrokersByAbbreviation(brokers, { requireAbbreviation = false } = {}) {
  const list = brokers ? [...brokers] : [];
  const filtered = requireAbbreviation
    ? list.filter((broker) => broker && broker.abbreviation)
    : list;
  return filtered.sort((a, b) =>
    (a.abbreviation || "").localeCompare(b.abbreviation || "")
  );
}

export function sortStrategiesForOrderDialog(strategies, { includeId = false } = {}) {
  if (!strategies?.length) return [];
  return [...strategies]
    .filter((s) =>
      includeId
        ? s && (s.name || s.strategyId || s.id)
        : s && (s.name || s.strategyId)
    )
    .sort((a, b) =>
      String(a.name || a.strategyId || "").localeCompare(
        String(b.name || b.strategyId || "")
      )
    );
}

export function findStrategyByFormValue(strategies, strategyValue) {
  return strategies?.find(
    (s) =>
      s.name === strategyValue ||
      s.strategyId === strategyValue ||
      s.id === strategyValue
  );
}

export function getFundAccountNames(funds, fundAbbreviation) {
  if (!fundAbbreviation || !funds) return [];
  const selectedFund = funds.find((fund) => fund.abbreviation === fundAbbreviation);
  if (!selectedFund?.accounts || Object.keys(selectedFund.accounts).length === 0) {
    return [];
  }
  return Object.keys(selectedFund.accounts).sort();
}

export function getInvestmentTypeFromAssetType(assetType) {
  return hashInvestmentReverse(String(assetType || "Equity").toLowerCase());
}

export function applyAssetTypeToForm(prev, nextAssetType) {
  const nextInvestmentType = getInvestmentTypeFromAssetType(nextAssetType);
  return {
    ...prev,
    assetType: nextAssetType,
    ...(nextInvestmentType === 10
      ? { option: prev.option ?? 1 }
      : { option: 1, strikePrice: "", expirationDate: "" }),
  };
}

export function parseTickerSymbolStockInput(newInputValue) {
  const slash = newInputValue.indexOf("/");
  const symbol =
    slash >= 0
      ? newInputValue.slice(0, slash).trim().toUpperCase()
      : newInputValue.trim().toUpperCase();
  const stock = slash >= 0 ? newInputValue.slice(slash + 1).trim() : "";
  return { symbol, stock };
}

export function validateOptionFormFields(formData, investmentType) {
  if (investmentType !== 10) return true;
  const strike = Number(formData.strikePrice);
  const expMs = new Date(formData.expirationDate).getTime();
  if (
    (formData.option !== 0 && formData.option !== 1) ||
    !Number.isFinite(strike) ||
    strike <= 0 ||
    !formData.expirationDate ||
    !Number.isFinite(expMs)
  ) {
    alert(
      "Please fill in all required option fields (Options, Strike Price, Expiration Date)"
    );
    return false;
  }
  return true;
}

export function buildOptionFieldsForOrder(formData, investmentType) {
  if (investmentType !== 10) return {};
  return {
    option: formData.option,
    strikePrice: Number(formData.strikePrice),
    expirationDate: new Date(formData.expirationDate).getTime(),
    underlying: {
      symbol: formData.ticker.toLowerCase(),
      currency: 7,
      exchange: 4,
      investment: 4,
    },
  };
}

export function buildOrderTickerPayload(formData, investmentType, optionFields = {}) {
  return {
    symbol: formData.ticker.toLowerCase(),
    ...(formData.stock?.trim() ? { stock: formData.stock.trim() } : {}),
    investment: investmentType !== null ? investmentType : 4,
    exchange: 4,
    currency: 7,
    ...optionFields,
  };
}

export function buildDistributionKey({
  account,
  fundId,
  isSmaFund,
  strategyRecord,
}) {
  let key = `${account}#${fundId}#${isSmaFund ? 1 : 0}`;
  if (isSmaFund) {
    key += `#${SMA_BUCKET_STRATEGY_ID}`;
  } else {
    const strategyKey = strategyRecord.strategyId || strategyRecord.id;
    key += `#${strategyKey}`;
  }
  return key;
}

/** New order: multiple distribution rows. */
export function buildDistributionFromRows({
  distributionRows,
  selectedFund,
  isSMA,
  selectedStrategy,
}) {
  const distribution = {};
  distributionRows.forEach((row) => {
    if (row.account && row.shareCount) {
      let distributionKey = `${row.account}#${selectedFund.fundId}#${isSMA}`;
      if (isSMA === 1) {
        distributionKey += `#${SMA_BUCKET_STRATEGY_ID}`;
      } else {
        const strategyId =
          selectedStrategy.strategyId || selectedStrategy.id;
        distributionKey += `#${strategyId}`;
      }
      distribution[distributionKey] = parseInt(row.shareCount, 10);
    }
  });
  return distribution;
}

export function resolveRoutingEnum(routing) {
  return routing === "Auto" ? 0 : 1;
}

export function resolveEditRoutingDisplay(routing) {
  return routing === 0 || routing === "0" ? "Auto" : "Manual";
}

export function validateNonSmaStrategy(formData, isSmaFund, strategies) {
  if (isSmaFund) return { ok: true, strategy: null };
  if (!formData.strategy) {
    alert("Please select a strategy");
    return { ok: false };
  }
  const strategy = findStrategyByFormValue(strategies, formData.strategy);
  if (!strategy) {
    alert("Invalid strategy selected");
    return { ok: false };
  }
  if (!strategy.strategyId && !strategy.id) {
    alert("Invalid strategy selected");
    return { ok: false };
  }
  return { ok: true, strategy };
}
